import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Search, Plus, Save, Check, X, Loader2, Trash2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Exercise configurations - Updated to match new scoring rules
const EXERCISE_CONFIG = {
  strength: {
    label: '근력',
    exercises: [
      { value: 'deadlift', label: '데드리프트', hasScore: true },
      { value: 'squat', label: '스쿼트', hasScore: true },
      { value: 'bench_press', label: '벤치프레스', hasScore: true },
      { value: 'shoulder_press', label: '숄더프레스', hasScore: true },
    ],
    columns: ['weight_kg', 'reps'],
    columnLabels: { weight_kg: '수행무게(kg)', reps: '반복횟수(1-5)' },
  },
  muscular_endurance: {
    label: '근지구력',
    exercises: [
      { value: 'pushup', label: '푸시업', hasScore: true },
      { value: 'situp', label: '싯업', hasScore: true },
      { value: 'barbell_squat_endurance', label: '바벨 스쿼트', hasScore: true, hasLoadInput: true },
      { value: 'barbell_deadlift_endurance', label: '바벨 데드리프트', hasScore: true, hasLoadInput: true },
      { value: 'barbell_shoulder_press_endurance', label: '바벨 숄더프레스', hasScore: true, hasLoadInput: true },
      { value: 'plank', label: '플랭크', hasScore: true, columns: ['seconds'] },
      { value: 'wall_squat', label: '월 스쿼트', hasScore: true, columns: ['seconds'] },
      { value: 'amrap1', label: 'AMRAP1', hasScore: true, isAmrap: true, columns: ['amrap_minutes', 'amrap_record'] },
      { value: 'amrap2', label: 'AMRAP2', hasScore: true, isAmrap: true, columns: ['amrap_minutes', 'amrap_record'] },
      { value: 'amrap3', label: 'AMRAP3', hasScore: true, isAmrap: true, columns: ['amrap_minutes', 'amrap_record', 'weight_kg', 'weight_adjustment'] },
      { value: 'amrap4', label: 'AMRAP4', hasScore: true, isAmrap: true, columns: ['amrap_minutes', 'amrap_record'] },
    ],
    columns: ['reps'],
    columnLabels: {
      reps: '횟수(3분)',
      input_load_kg: '사용중량(kg)',
      seconds: '유지시간(초)',
      amrap_minutes: '시간(분)',
      amrap_record: '기록 (X.YY)',
      weight_kg: '사용무게(kg)',
      weight_adjustment: '무게보정(점)',
    },
  },
  cardio: {
    label: '심폐지구력',
    exercises: [
      { value: 'running', label: '러닝', hasScore: true },
      { value: 'rowing', label: '로잉', hasScore: true },
    ],
    columns: ['distance_m', 'duration_seconds'],
    columnLabels: { distance_m: '거리(m)', duration_seconds: '시간(초)' },
  },
  inbody: {
    label: '인바디',
    exercises: [{ value: 'inbody', label: '인바디' }],
    columns: ['weight_kg', 'body_fat_pct', 'skeletal_muscle_kg', 'inbody_score'],
    columnLabels: { 
      weight_kg: '체중(kg)', 
      body_fat_pct: '체지방률(%)', 
      skeletal_muscle_kg: '골격근량(kg)',
      inbody_score: '인바디점수'
    },
  },
  posture: {
    label: '체형/자세',
    exercises: [{ value: 'posture', label: '자세평가' }],
    columns: ['posture_score', 'memo'],
    columnLabels: { posture_score: '자세점수(0-100)', memo: '메모' },
  },
};

const BatchEntry = () => {
  // State
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dataType, setDataType] = useState('exercise'); // 'exercise' | 'measurement'
  const [category, setCategory] = useState('strength');
  const [exercise, setExercise] = useState('barbell_deadlift');
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'needsSave'
  const [validationErrors, setValidationErrors] = useState({}); // { rowIndex: { field: errorMsg } }
  
  // 최근 측정 기록 (50건) - 일괄 삭제 지원
  const [recentMeasurements, setRecentMeasurements] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [selectedRecentIds, setSelectedRecentIds] = useState(new Set());
  const [recentDeleteOpen, setRecentDeleteOpen] = useState(false);
  const [recentDeleting, setRecentDeleting] = useState(false);
  
  const searchInputRef = useRef(null);
  const tableRef = useRef(null);

  // ========== 입력값 유효성 검증 함수 ==========
  const validateInput = (field, value, cat = category) => {
    if (value === '' || value === null || value === undefined) return null;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '숫자를 입력해주세요';
    
    // 체중 검증
    if (field === 'bw_kg' || field === 'weight_kg' && cat === 'inbody') {
      if (numValue < 30 || numValue > 200) {
        return '체중: 30~200kg';
      }
    }
    
    // 근력 - 무게 검증
    if (field === 'weight_kg' && cat === 'strength') {
      if (numValue < 1 || numValue > 500) {
        return '무게: 1~500kg';
      }
    }
    
    // 근력 - 반복수 검증 (1-20)
    if (field === 'reps' && cat === 'strength') {
      if (numValue < 1 || numValue > 20) {
        return '반복수: 1~20회';
      }
    }
    
    // 근지구력 - 반복수 검증 (1-500)
    if (field === 'reps' && cat === 'muscular_endurance') {
      if (numValue < 1 || numValue > 500) {
        return '반복수: 1~500회';
      }
    }
    
    // 근지구력 - 무게 검증
    if (field === 'input_load_kg' && cat === 'muscular_endurance') {
      if (numValue < 0 || numValue > 500) {
        return '무게: 0~500kg';
      }
    }

    // 근지구력 - 유지시간(초) 검증 (plank, wall_squat)
    if (field === 'seconds' && cat === 'muscular_endurance') {
      if (numValue < 1 || numValue > 3600) {
        return '시간: 1~3600초';
      }
    }
    
    // 심폐 - 거리 검증
    if (field === 'distance_m' && cat === 'cardio') {
      if (numValue < 1) {
        return '거리: 1m 이상';
      }
    }

    // 심폐 - 시간(초) 검증
    if (field === 'duration_seconds') {
      if (numValue < 10 || numValue > 3600) {
        return '시간: 10~3600초';
      }
    }
    
    // 인바디 - 체지방률 검증
    if (field === 'body_fat_pct') {
      if (numValue < 3 || numValue > 60) {
        return '체지방률: 3~60%';
      }
    }
    
    // 인바디 - 골격근량 검증
    if (field === 'skeletal_muscle_kg') {
      if (numValue < 10 || numValue > 100) {
        return '골격근량: 10~100kg';
      }
    }
    
    // 자세 점수 검증
    if (field === 'posture_score') {
      if (numValue < 0 || numValue > 100) {
        return '점수: 0~100';
      }
    }

    // AMRAP3 사용무게(kg) 검증
    if (field === 'weight_kg' && cat === 'muscular_endurance') {
      if (numValue < 0 || numValue > 200) {
        return '무게: 0~200kg';
      }
    }

    // AMRAP3 무게보정(점) 검증 — 음수 허용
    if (field === 'weight_adjustment' && cat === 'muscular_endurance') {
      if (numValue < -50 || numValue > 50) {
        return '무게보정: -50 ~ +50';
      }
    }
    
    return null; // 유효함
  };

  // 행의 모든 필드 검증
  const validateRow = (row, rowIndex) => {
    const errors = {};
    const columns = getColumns();
    
    columns.forEach(col => {
      const value = row[col];
      const error = validateInput(col, value);
      if (error) {
        errors[col] = error;
      }
    });
    
    return errors;
  };

  // 전체 행 검증 및 저장 가능 여부 확인
  const validateAllRows = () => {
    const allErrors = {};
    let hasErrors = false;
    
    rows.forEach((row, index) => {
      const rowErrors = validateRow(row, index);
      if (Object.keys(rowErrors).length > 0) {
        allErrors[index] = rowErrors;
        hasErrors = true;
      }
    });
    
    setValidationErrors(allErrors);
    return !hasErrors;
  };

  // 입력값 변경 시 실시간 검증
  const handleInputChange = (rowIndex, field, value) => {
    // 값 업데이트 (기존 updateRowValue 로직)
    setRows(prev => prev.map((row, idx) => {
      if (idx !== rowIndex) return row;
      const newMetrics = { ...row.raw_metrics, [field]: value };
      return { ...row, raw_metrics: newMetrics, status: 'pending' };
    }));
    setSaveStatus('needsSave');
    
    // 실시간 검증
    const error = validateInput(field, value);
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (!newErrors[rowIndex]) newErrors[rowIndex] = {};
      
      if (error) {
        newErrors[rowIndex][field] = error;
      } else {
        delete newErrors[rowIndex][field];
        if (Object.keys(newErrors[rowIndex]).length === 0) {
          delete newErrors[rowIndex];
        }
      }
      return newErrors;
    });
  };
  // ============================================

  // Get current config
  const currentConfig = EXERCISE_CONFIG[category];
  const currentExercise = currentConfig?.exercises?.find(e => e.value === exercise);
  // All exercises now have scoring
  const hasScoring = currentExercise?.hasScore !== false;

  // Columns based on category and exercise
  const getColumns = () => {
    // Per-exercise override takes priority
    if (currentExercise?.columns) {
      return currentExercise.columns;
    }

    let cols = currentConfig?.columns || [];

    // For barbell endurance exercises (with load input), add input_load_kg column
    if (category === 'muscular_endurance' && currentExercise?.hasLoadInput) {
      cols = ['reps', 'input_load_kg'];
    }

    return cols;
  };

  // Search members
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await apiClient.get('/admin/members/search', { params: { q: query } });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // ========== 최근 측정 기록 ==========
  const fetchRecentMeasurements = useCallback(async () => {
    setRecentLoading(true);
    try {
      const res = await apiClient.get('/admin/measurements/recent', { params: { limit: 50 } });
      setRecentMeasurements(res.data?.measurements || []);
    } catch (error) {
      console.error('Failed to fetch recent measurements:', error);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentMeasurements();
  }, [fetchRecentMeasurements]);

  const toggleRecentSelect = (id) => {
    setSelectedRecentIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleRecentSelectAll = () => {
    if (selectedRecentIds.size === recentMeasurements.length && recentMeasurements.length > 0) {
      setSelectedRecentIds(new Set());
    } else {
      setSelectedRecentIds(new Set(recentMeasurements.map(m => m.id)));
    }
  };

  const handleBulkDeleteRecent = async () => {
    if (selectedRecentIds.size === 0) return;
    setRecentDeleting(true);
    try {
      const ids = Array.from(selectedRecentIds);
      const res = await apiClient.post('/admin/measurements/bulk-delete', { ids });
      toast.success(`${res.data?.deleted_count ?? ids.length}건 삭제 완료`);
      setSelectedRecentIds(new Set());
      setRecentDeleteOpen(false);
      await fetchRecentMeasurements();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('삭제 실패: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRecentDeleting(false);
    }
  };

  // Add member to rows
  const addMember = async (member) => {
    // Check if already added
    if (rows.some(r => r.member_id === member._id)) {
      toast.error('이미 추가된 회원입니다');
      return;
    }

    // Calculate age
    let age = 30;
    if (member.birth_date) {
      const birth = new Date(member.birth_date);
      const today = new Date();
      age = today.getFullYear() - birth.getFullYear();
    }

    const newRow = {
      id: `${member._id}-${Date.now()}`,
      member_id: member._id,
      member_number: member.member_number,
      member_name: member.name,
      gender: member.gender,
      age,
      raw_metrics: {},
      computed_metrics: {},
      score: null,
      status: 'pending', // pending | saving | success | error
      error: null,
    };

    // Apply common values from first row
    if (rows.length > 0) {
      const firstRow = rows[0];
      const columns = getColumns();
      columns.forEach(col => {
        if (firstRow.raw_metrics[col] !== undefined && col !== 'memo') {
          newRow.raw_metrics[col] = firstRow.raw_metrics[col];
        }
      });
    }

    setRows(prev => [...prev, newRow]);
    setSaveStatus('needsSave');
    setSearchQuery('');
    setSearchResults([]);
  };

  // Update row value
  const updateRowValue = (rowId, field, value) => {
    setRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      
      const newMetrics = { ...row.raw_metrics, [field]: value };
      return { ...row, raw_metrics: newMetrics, status: 'pending' };
    }));
    setSaveStatus('needsSave');
  };

  // Remove row
  const removeRow = (rowId) => {
    setRows(prev => prev.filter(r => r.id !== rowId));
  };

  // Save all rows
  const handleSaveAll = async () => {
    if (rows.length === 0) {
      toast.error('저장할 데이터가 없습니다');
      return;
    }

    // ========== 저장 전 검증 ==========
    if (!validateAllRows()) {
      toast.error('입력값을 확인해주세요. 잘못된 값이 있습니다.');
      return;
    }
    // ==================================

    // Get pending rows BEFORE updating state (to avoid race condition)
    const pendingRows = rows.filter(r => r.status === 'pending');
    
    if (pendingRows.length === 0) {
      toast.info('저장할 새 데이터가 없습니다');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');

    // Mark all pending rows as saving
    setRows(prev => prev.map(r => r.status === 'pending' ? { ...r, status: 'saving' } : r));

    try {
      const payload = {
        date,
        category,
        exercise,
        rows: pendingRows.map(r => ({
          member_id: r.member_id,
          member_number: r.member_number,
          raw_metrics: r.raw_metrics,
          measured_at: `${date}T00:00:00Z`,
        })),
      };

      const response = await apiClient.post('/admin/measurements/batch', payload);
      const results = response.data.results;

      // Update rows with results - match by member_id from pendingRows
      const pendingMemberIds = pendingRows.map(r => r.member_id);
      setRows(prev => prev.map(row => {
        if (!pendingMemberIds.includes(row.member_id)) return row;
        
        const result = results.find(r => 
          r.member_number === row.member_number || r.member_name === row.member_name
        );
        
        if (result) {
          if (result.status === 'success') {
            return {
              ...row,
              status: 'success',
              score: result.score,
              computed_metrics: result.computed_metrics || {},
              error: null,
            };
          } else if (result.status === 'skipped') {
            return {
              ...row,
              status: 'success',
              error: result.message,
            };
          } else {
            return {
              ...row,
              status: 'error',
              error: result.message,
            };
          }
        }
        return { ...row, status: 'error', error: '결과를 찾을 수 없음' };
      }));

      const successCount = results.filter(r => r.status === 'success' || r.status === 'skipped').length;
      const errorCount = results.filter(r => r.status === 'error').length;
      
      // Check for score calculation failures
      const scoreFailures = results.filter(r => r.status === 'success' && (r.score === null || r.score === undefined));
      if (scoreFailures.length > 0) {
        const failedNames = scoreFailures.map(r => r.member_name || r.member_number).join(', ');
        toast.warning(`점수 계산 실패: ${failedNames} - 기준표 데이터가 누락되었거나 입력 값이 부족합니다.`, {
          duration: 5000,
        });
      }

      if (errorCount === 0) {
        toast.success(`${successCount}건 저장 완료`);
        setSaveStatus('saved');
      } else {
        toast.warning(`${successCount}건 성공, ${errorCount}건 실패`);
        setSaveStatus('needsSave');
      }
      // 저장 후 최근 측정 기록 새로고침
      fetchRecentMeasurements();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('저장 실패: ' + (error.response?.data?.detail || error.message));
      const pendingMemberIds = pendingRows.map(r => r.member_id);
      setRows(prev => prev.map(r => pendingMemberIds.includes(r.member_id) ? { ...r, status: 'error', error: '저장 실패' } : r));
      setSaveStatus('needsSave');
    } finally {
      setSaving(false);
    }
  };

  // Handle key navigation in table
  const handleKeyDown = (e, rowIndex, colIndex) => {
    const columns = getColumns();
    
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      
      // Move to next cell
      let nextRowIndex = rowIndex;
      let nextColIndex = colIndex + 1;
      
      if (nextColIndex >= columns.length) {
        nextColIndex = 0;
        nextRowIndex = rowIndex + 1;
      }
      
      if (nextRowIndex < rows.length) {
        const nextInput = document.querySelector(
          `[data-row="${nextRowIndex}"][data-col="${nextColIndex}"]`
        );
        nextInput?.focus();
      }
    }
  };

  // Reset when category/exercise changes
  useEffect(() => {
    if (EXERCISE_CONFIG[category]) {
      const firstExercise = EXERCISE_CONFIG[category].exercises[0];
      setExercise(firstExercise.value);
    }
    setRows([]);
    setSaveStatus('idle');
  }, [category]);

  useEffect(() => {
    // Reset rows when exercise changes
    setRows([]);
    setSaveStatus('idle');
  }, [exercise]);

  // Render input field based on column type
  const renderInput = (row, rowIndex, col, colIndex) => {
    const value = row.raw_metrics[col] ?? '';
    const error = validationErrors[rowIndex]?.[col];

    // amrap_record는 X.YY 형식이므로 0.01 단위 입력 허용
    const isAmrapRecord = col === 'amrap_record';
    const stepValue = isAmrapRecord
      ? '0.01'
      : (col.includes('pct') || col === 'posture_score' ? '0.1' : '1');
    const placeholder = isAmrapRecord ? '예: 5.12 (5R+12reps)' : (col === 'amrap_minutes' ? '예: 8' : '');

    return (
      <div className="relative">
        <Input
          type={col === 'memo' ? 'text' : 'number'}
          step={stepValue}
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleInputChange(rowIndex, col, e.target.value ? Number(e.target.value) : '')}
          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
          data-row={rowIndex}
          data-col={colIndex}
          className={`w-full h-8 text-sm ${
            error ? 'border-red-500 bg-red-50' : 
            row.status === 'error' ? 'border-red-500' : ''
          }`}
          disabled={row.status === 'saving' || row.status === 'success'}
        />
        {error && (
          <div className="absolute left-0 top-full mt-0.5 text-xs text-red-500 whitespace-nowrap z-10">
            {error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date */}
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">날짜</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-40"
              />
            </div>

            {/* Data Type Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={dataType === 'exercise' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setDataType('exercise');
                  setCategory('strength');
                }}
              >
                운동 데이터
              </Button>
              <Button
                variant={dataType === 'measurement' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setDataType('measurement');
                  setCategory('inbody');
                }}
              >
                측정 데이터
              </Button>
            </div>

            {/* Category Select */}
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">카테고리</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dataType === 'exercise' ? (
                    <>
                      <SelectItem value="strength">근력</SelectItem>
                      <SelectItem value="muscular_endurance">근지구력</SelectItem>
                      <SelectItem value="cardio">심폐지구력</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="inbody">인바디</SelectItem>
                      <SelectItem value="posture">체형/자세</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Exercise Select */}
            {currentConfig?.exercises?.length > 1 && (
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">종목</Label>
                <Select value={exercise} onValueChange={setExercise}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currentConfig.exercises.map(ex => (
                      <SelectItem key={ex.value} value={ex.value}>
                        {ex.label} {ex.hasScore === false && '(미계산)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Save Status & Button */}
            <div className="flex items-center gap-2 ml-auto">
              <span className={`text-sm ${
                saveStatus === 'saved' ? 'text-green-600' :
                saveStatus === 'needsSave' ? 'text-orange-600' :
                saveStatus === 'saving' ? 'text-blue-600' : 'text-gray-500'
              }`}>
                {saveStatus === 'saved' && '저장 완료'}
                {saveStatus === 'needsSave' && '저장 필요'}
                {saveStatus === 'saving' && '저장 중...'}
                {saveStatus === 'idle' && '대기'}
              </span>
              {Object.keys(validationErrors).length > 0 && (
                <span className="text-sm text-red-600 font-medium">
                  입력 오류 {Object.keys(validationErrors).length}건
                </span>
              )}
              <Button
                onClick={handleSaveAll}
                disabled={saving || rows.length === 0 || saveStatus === 'saved' || Object.keys(validationErrors).length > 0}
                className="gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                전체 저장
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Member Search */}
        <Card className="mb-6">
          <CardHeader className="py-3">
            <CardTitle className="text-lg">회원 추가</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                ref={searchInputRef}
                className="pl-10"
                placeholder="이름, 전화번호, 회원번호로 검색 후 엔터..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults.length > 0) {
                    addMember(searchResults[0]);
                  }
                }}
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin" />
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                {searchResults.map((member) => (
                  <div
                    key={member._id}
                    className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center border-b last:border-b-0"
                    onClick={() => addMember(member)}
                  >
                    <div>
                      <span className="font-medium">{member.name}</span>
                      <span className="text-gray-500 text-sm ml-2">
                        {member.member_number} · {member.phone}
                      </span>
                    </div>
                    <Button size="sm" variant="ghost">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Score Info Banner */}
        <div className={`mb-4 p-3 rounded-lg text-sm ${hasScoring ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>
          {hasScoring ? (
            <>✅ <strong>{currentExercise?.label || exercise}</strong>: 점수 자동 계산됨</>
          ) : (
            <>⚠️ <strong>{currentExercise?.label || exercise}</strong>: 점수 미구현 (raw 데이터만 저장됨)</>
          )}
        </div>

        {/* AMRAP 입력 안내 */}
        {currentExercise?.isAmrap && (
          <div
            data-testid="amrap-help-banner"
            className="mb-4 p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-900"
          >
            <div className="font-medium mb-1">📝 AMRAP 기록 입력 가이드</div>
            <div className="text-xs space-y-0.5">
              <div>• <strong>시간</strong>: 측정 시간(분). 예) 8, 10, 12</div>
              <div>• <strong>기록</strong>: <code>X.YY</code> 형식 (X = 완료 라운드, YY = 마지막 라운드 reps)</div>
              <div className="text-amber-800">
                예) <code>5.12</code> = 5R + 12 reps · <code>7.04</code> = 7R + 4 reps · <code>6.30</code> = 6R + 30 reps
              </div>
              <div className="text-amber-700/80 mt-1">
                ℹ️ 점수 계산 시에는 소수점 아래 숫자가 ×3 보정되어 기준표와 비교됩니다 (예: 5.12 → 5.36).
              </div>
            </div>
          </div>
        )}

        {/* Data Entry Table */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-lg">
              입력 테이블 ({rows.length}명)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto" ref={tableRef}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="p-2 text-left font-medium text-gray-700 w-20">회원번호</th>
                    <th className="p-2 text-left font-medium text-gray-700 w-24">이름</th>
                    <th className="p-2 text-left font-medium text-gray-700 w-16">성별</th>
                    <th className="p-2 text-left font-medium text-gray-700 w-16">나이</th>
                    {getColumns().map(col => (
                      <th key={col} className="p-2 text-left font-medium text-gray-700 w-28">
                        {currentConfig?.columnLabels?.[col] || col}
                      </th>
                    ))}
                    {/* Computed columns for strength */}
                    {category === 'strength' && (
                      <>
                        <th className="p-2 text-left font-medium text-gray-700 w-24">1RM 추정</th>
                        <th className="p-2 text-left font-medium text-gray-700 w-24">상대근력</th>
                      </>
                    )}
                    {category === 'cardio' && (
                      <th className="p-2 text-left font-medium text-gray-700 w-24">VO2max</th>
                    )}
                    <th className="p-2 text-left font-medium text-gray-700 w-20">점수</th>
                    <th className="p-2 text-left font-medium text-gray-700 w-20">상태</th>
                    <th className="p-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={10 + getColumns().length} className="p-8 text-center text-gray-500">
                        위 검색창에서 회원을 검색하여 추가하세요
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, rowIndex) => (
                      <tr 
                        key={row.id} 
                        className={`border-b ${
                          row.status === 'success' ? 'bg-green-50' :
                          row.status === 'error' ? 'bg-red-50' :
                          row.status === 'saving' ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="p-2">{row.member_number}</td>
                        <td className="p-2 font-medium">{row.member_name}</td>
                        <td className="p-2">{row.gender === 'male' ? '남' : '여'}</td>
                        <td className="p-2">{row.age}</td>
                        {getColumns().map((col, colIndex) => (
                          <td key={col} className="p-2">
                            {renderInput(row, rowIndex, col, colIndex)}
                          </td>
                        ))}
                        {/* Computed metrics */}
                        {category === 'strength' && (
                          <>
                            <td className="p-2 text-gray-600">
                              {row.computed_metrics?.one_rm || '-'}
                            </td>
                            <td className="p-2 text-gray-600">
                              {row.computed_metrics?.relative_strength 
                                ? row.computed_metrics.relative_strength.toFixed(2) 
                                : '-'}
                            </td>
                          </>
                        )}
                        {category === 'cardio' && (
                          <td className="p-2 text-gray-600">
                            {row.computed_metrics?.vo2 || '-'}
                          </td>
                        )}
                        <td className="p-2 font-medium">
                          {row.score !== null && row.score !== undefined ? (
                            <span className="text-blue-600">{row.score}</span>
                          ) : (
                            <span className="text-gray-400">미계산</span>
                          )}
                        </td>
                        <td className="p-2">
                          {row.status === 'saving' && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                          {row.status === 'success' && <Check className="h-4 w-4 text-green-500" />}
                          {row.status === 'error' && (
                            <div className="flex items-center gap-1">
                              <X className="h-4 w-4 text-red-500" />
                              <span className="text-xs text-red-600">{row.error}</span>
                            </div>
                          )}
                          {row.status === 'pending' && <span className="text-gray-400 text-xs">대기</span>}
                        </td>
                        <td className="p-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeRow(row.id)}
                            disabled={row.status === 'saving'}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Keyboard Shortcuts Help */}
        <div className="mt-4 text-sm text-gray-500">
          <span className="font-medium">단축키:</span> Tab/Enter로 다음 셀 이동
        </div>

        {/* ========== 최근 측정 기록 (50건) ========== */}
        <Card className="mt-6">
          <CardHeader className="py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                최근 측정 기록 <span className="text-gray-400 font-normal text-sm">(최대 50건)</span>
              </CardTitle>
              <div className="flex items-center gap-2">
                {selectedRecentIds.size > 0 && (
                  <span className="text-sm text-gray-600" data-testid="recent-selected-count">
                    {selectedRecentIds.size}건 선택됨
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchRecentMeasurements}
                  disabled={recentLoading}
                  data-testid="recent-refresh-btn"
                >
                  {recentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : '새로고침'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setRecentDeleteOpen(true)}
                  disabled={selectedRecentIds.size === 0 || recentDeleting}
                  data-testid="recent-bulk-delete-btn"
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  선택 삭제
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {recentMeasurements.length === 0 ? (
              <p className="text-center text-gray-500 py-8 text-sm">
                {recentLoading ? '불러오는 중...' : '측정 기록이 없습니다.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left w-10">
                        <Checkbox
                          checked={selectedRecentIds.size === recentMeasurements.length && recentMeasurements.length > 0}
                          onCheckedChange={toggleRecentSelectAll}
                          data-testid="recent-select-all-checkbox"
                        />
                      </th>
                      <th className="p-2 text-left">측정일</th>
                      <th className="p-2 text-left">회원</th>
                      <th className="p-2 text-left">카테고리</th>
                      <th className="p-2 text-left">종목</th>
                      <th className="p-2 text-left">측정값</th>
                      <th className="p-2 text-left">점수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentMeasurements.map((m) => {
                      const categoryName = {
                        strength: '근력',
                        muscular_endurance: '근지구력',
                        cardio: '심폐지구력',
                        inbody: '체성분',
                        posture: '자세'
                      }[m.category] || m.category;
                      const exName = (m.exercise && {
                        deadlift: '데드리프트', squat: '스쿼트', bench_press: '벤치프레스',
                        shoulder_press: '숄더프레스', pushup: '푸시업', situp: '싯업',
                        barbell_squat_endurance: '바벨 스쿼트',
                        barbell_deadlift_endurance: '바벨 데드리프트',
                        barbell_shoulder_press_endurance: '바벨 숄더프레스',
                        plank: '플랭크', wall_squat: '월 스쿼트',
                        amrap1: 'AMRAP1', amrap2: 'AMRAP2', amrap3: 'AMRAP3', amrap4: 'AMRAP4',
                        running: '러닝', rowing: '로잉 퍼포먼스',
                        inbody: '인바디', posture: '자세평가',
                      }[m.exercise]) || m.exercise || '-';
                      // 측정값 요약
                      const rm = m.raw_metrics || {};
                      let valDisplay = '-';
                      if (rm.weight_kg && rm.reps) valDisplay = `${rm.weight_kg}kg × ${rm.reps}회`;
                      else if (rm.distance_m && rm.duration_seconds) valDisplay = `${rm.distance_m}m / ${rm.duration_seconds}초`;
                      else if (rm.reps && rm.input_load_kg) valDisplay = `${rm.reps}회 × ${rm.input_load_kg}kg`;
                      else if (rm.reps) valDisplay = `${rm.reps}회`;
                      else if (rm.seconds) valDisplay = `${rm.seconds}초`;
                      else if (rm.posture_score !== undefined) valDisplay = `${rm.posture_score}점`;
                      else if (rm.body_fat_pct !== undefined) valDisplay = `체지방 ${rm.body_fat_pct}%`;
                      
                      const dateStr = m.measured_at ? String(m.measured_at).split('T')[0] : '-';
                      const isSelected = selectedRecentIds.has(m.id);
                      return (
                        <tr
                          key={m.id}
                          className={`border-b hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                          data-testid={`recent-row-${m.id}`}
                        >
                          <td className="p-2">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleRecentSelect(m.id)}
                              data-testid={`recent-checkbox-${m.id}`}
                            />
                          </td>
                          <td className="p-2 text-gray-600">{dateStr}</td>
                          <td className="p-2 font-medium">
                            {m.member_name || '-'}
                            {m.member_number && <span className="text-gray-400 ml-1 text-xs">({m.member_number})</span>}
                          </td>
                          <td className="p-2">{categoryName}</td>
                          <td className="p-2">{exName}</td>
                          <td className="p-2 text-gray-600">{valDisplay}</td>
                          <td className="p-2">
                            {m.score != null ? (
                              <span className="font-medium text-blue-600">{Number(m.score).toFixed(1)}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 일괄 삭제 확인 다이얼로그 */}
      <Dialog open={recentDeleteOpen} onOpenChange={setRecentDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>측정 데이터 일괄 삭제</DialogTitle>
            <DialogDescription>
              선택한 <span className="font-semibold text-red-600">{selectedRecentIds.size}건</span>의 측정 데이터를 삭제합니다. 삭제 후에는 복구할 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRecentDeleteOpen(false)}
              disabled={recentDeleting}
            >
              취소
            </Button>
            <Button
              onClick={handleBulkDeleteRecent}
              disabled={recentDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-bulk-delete-btn"
            >
              {recentDeleting ? '삭제 중...' : `${selectedRecentIds.size}건 삭제`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BatchEntry;
